/**
 * 排版任务全局状态模块
 *
 * 侧栏 Badge 未完成任务数、排版任务列表页、创建任务动效共用同一份 taskList，
 * 避免各页面各自维护导致数量不一致。
 */
import {
  fetchPaibanTaskList,
  sendToPaibanTaskList,
  updatePaibanTask,
} from '@/apis/paibanTask'
import {
  FetchPaibanTaskListPayload,
  PaibanTaskRow,
  PaibanTaskStatusEnum,
  SendToPaibanTaskPayload,
  UpdatePaibanTaskPayload,
  statusMap,
} from '@/views/paibanTask/types'
import Vue from 'vue'
import type { ActionContext, Module } from 'vuex'

import type { IRootState } from '../types'

const POLL_INTERVAL = 3000

let pollTimer: ReturnType<typeof setTimeout> | null = null
let isPollingActive = false

export interface PaibanTaskListQuery {
  order_no?: string
  page: number
  limit: number
  status?: FetchPaibanTaskListPayload['status']
}

/** 列表中是否存在等待中/排版中的任务 */
export function hasTaskPendingOrRunningInList(
  taskList: PaibanTaskRow[]
): boolean {
  return taskList.some((item) =>
    [PaibanTaskStatusEnum.RUNNING, PaibanTaskStatusEnum.PENDING].includes(
      Number(item.taskStatus) as PaibanTaskStatusEnum
    )
  )
}

/**
 * 是否为「未完成」任务（侧栏角标统计口径）
 * 已完成不计入；等待中、排版中、失败均计入
 */
export function isUnfinishedPaibanTask(status: PaibanTaskStatusEnum): boolean {
  return status !== PaibanTaskStatusEnum.COMPLETED
}

/**
 * 生成任务批次号，格式：PB-YYYYMMDD-序号（三位随机）
 * 与列表 mock 数据命名风格保持一致，便于后续对接真实接口
 */
export function genPaibanTaskBatch(): string {
  const d = new Date()
  const pad = (n: number) => String(n).padStart(2, '0')
  const dateStr = `${d.getFullYear()}${pad(d.getMonth() + 1)}${pad(
    d.getDate()
  )}`
  const seq = String(Math.floor(Math.random() * 900) + 100)
  return `PB-${dateStr}-${seq}`
}

/**
 * 从全局 state 提取关联订单号，用于新建任务展示
 * 优先订单信息，其次样表工厂单号，均无则显示 '-'
 */
export function getRelatedOrderNosFromStore(rootState: IRootState): string {
  const orderInfo = rootState.orderInfo as { orderNo?: string }
  if (orderInfo?.orderNo) return String(orderInfo.orderNo)
  const sample = rootState.sampleTableData
  if (sample?.factoryOrderNum) return String(sample.factoryOrderNum)
  return '-'
}

/**
 * 构造一条待排版的任务记录（创建任务按钮提交成功后写入 store）
 */
export async function createPendingPaibanTask(
  payload: SendToPaibanTaskPayload
): Promise<{ task_batch: string }> {
  const res = await sendToPaibanTaskList(payload)
  if (res && res.status !== 1) {
    const msg = res.msg || '创建任务失败'
    Vue.prototype.$message.error(msg)
  }
  return res.data
}

/** 将接口返回数据转换为任务记录 */
export async function dealDataToPaibanTaskRow(
  list: any[]
): Promise<PaibanTaskRow[]> {
  const taskRowList = list.map(async (data) => {
    const taskRow: PaibanTaskRow = {
      id: '',
      taskBatch: '',
      relatedOrderNos: '',
      taskStatus: PaibanTaskStatusEnum.PENDING,
      progress: 0,
      boardCount: null,
      optimizeRate: null,
      optimizedArea: null,
      productionLine: null,
      failedReason: null,
    }
    if (data) {
      const taskStatus = data.status as keyof typeof statusMap
      let progress = 0
      switch (taskStatus) {
        case PaibanTaskStatusEnum.RUNNING:
        case PaibanTaskStatusEnum.COMPLETED:
          progress = 100
          break
        default:
          progress = 100
          break
      }

      const resultUrlArr = data.result_url
        ? data.result_url
            .split(',')
            .map(
              (str: string) =>
                'https://eggrj.oss-cn-hangzhou.aliyuncs.com/' + str
            )
        : []
      const requestUrl = data.request_url
        ? 'https://eggrj.oss-cn-hangzhou.aliyuncs.com/' + data.request_url
        : ''

      let msg = data.msg
      try {
        const errJson = JSON.parse(msg)
        const errKeyArr = Object.keys(errJson).filter(
          (key) => errJson[key] && errJson[key].code == '2'
        )
        msg = Array.from(
          new Set(
            errKeyArr
              .map((key) => errJson[key].message)
              .flat(Infinity)
              .filter((ms) => ms.status == 'error')
              .map((ms) => ms.message)
          )
        ).join('、')
      } catch {
        if (data.msg.includes('板件超尺')) {
          msg = '板件超尺'
        } else {
          msg = data.msg
        }
      }

      taskRow.id = data.task_id
      taskRow.taskBatch = data.task_batch
      taskRow.relatedOrderNos = data.order_nos
      taskRow.FGNo = data.FGNo
      taskRow.taskStatus = taskStatus as PaibanTaskStatusEnum
      taskRow.progress = progress
      taskRow.boardCount = data.board_count
      taskRow.optimizeRate = data.used_rate
      taskRow.optimizedArea = data.used_area
      taskRow.productionLine = data.production_line_id
      taskRow.failedReason = msg
      taskRow.plank_url = data.plank_url
      taskRow.resultUrlArr = resultUrlArr
      taskRow.request_url = requestUrl
      taskRow.finished_time = data.finished_time
      taskRow.create_time = data.create_time
      taskRow.task_ids = data.task_ids
      taskRow.started_time = data.started_time
      taskRow.queue_position = data.queue_position
      taskRow.production_line_id = data.production_line_id
      taskRow.production_line_name = data.production_line_name
    }
    return taskRow
  })

  return await Promise.all(taskRowList)
}

export interface PaibanTaskStoreState {
  /** 排版任务列表（最近一周数据，与任务页表格同源） */
  taskList: PaibanTaskRow[]
  total: number
  isLoading: boolean
  liveTotal: number
  /** 列表查询参数，全局轮询复用 */
  listQuery: PaibanTaskListQuery
}

const paibanTaskStore: Module<PaibanTaskStoreState, IRootState> = {
  namespaced: true,
  state: () => ({
    // 深拷贝 mock，避免直接修改 types/data 中的常量
    taskList: [],
    total: 0,
    isLoading: false,
    liveTotal: 0,
    listQuery: {
      page: 1,
      limit: 10,
    },
  }),
  getters: {
    /** 侧栏「排版任务」菜单角标：未完成条数 */
    unfinishedCount(state): number {
      return state.taskList.filter((item) =>
        isUnfinishedPaibanTask(item.taskStatus)
      ).length
    },
    /** 排版中和等待中的任务条数 */
    runningAndPendingCount(state): number {
      return state.liveTotal
    },
    /** 当前列表是否存在等待中/排版中的任务，用于控制全局轮询 */
    hasTaskPendingOrRunning(state): boolean {
      return hasTaskPendingOrRunningInList(state.taskList)
    },
  },
  mutations: {
    /** 整表替换（筛选、同步接口等场景） */
    setTaskList(state, list: PaibanTaskRow[]) {
      state.taskList = list
    },
    /** 新建任务插入队首，与列表「最新在上」展示一致 */
    addTask(state, task: PaibanTaskRow) {
      state.taskList.unshift(task)
    },
    setTotal(state, total: number) {
      state.total = total
    },
    setLiveTotal(state, liveTotal: number) {
      state.liveTotal = liveTotal
    },
    /** 删除任务后角标会自动随 getter 重算 */
    removeTask(state, id: PaibanTaskRow['id']) {
      state.taskList = state.taskList.filter((item) => item.id !== id)
    },
    setIsLoading(state, isLoading: boolean) {
      state.isLoading = isLoading
    },
    setListQuery(state, query: Partial<PaibanTaskListQuery>) {
      state.listQuery = {
        ...state.listQuery,
        ...query,
      }
    },
  },

  actions: {
    stopTaskListPolling() {
      isPollingActive = false
      if (pollTimer !== null) {
        clearTimeout(pollTimer)
        pollTimer = null
      }
    },
    async pollTaskList(
      context: ActionContext<PaibanTaskStoreState, IRootState>
    ) {
      if (!isPollingActive) return

      try {
        await context.dispatch('fetchPaibanTaskList', {
          ...context.state.listQuery,
          isFetchInterval: true,
        })
      } catch {
        // 轮询失败不中断后续轮询
      }

      if (!isPollingActive) return

      if (context.getters.hasTaskPendingOrRunning) {
        pollTimer = setTimeout(() => {
          context.dispatch('pollTaskList')
        }, POLL_INTERVAL)
      } else {
        context.dispatch('stopTaskListPolling')
      }
    },
    startTaskListPolling(
      context: ActionContext<PaibanTaskStoreState, IRootState>
    ) {
      if (isPollingActive) return

      isPollingActive = true
      context.dispatch('pollTaskList')
    },
    syncTaskListPolling(
      context: ActionContext<PaibanTaskStoreState, IRootState>
    ) {
      if (context.getters.hasTaskPendingOrRunning) {
        context.dispatch('startTaskListPolling')
      } else {
        context.dispatch('stopTaskListPolling')
      }
    },
    async fetchPaibanTaskList(
      context,
      payload: FetchPaibanTaskListPayload & { isFetchInterval?: boolean }
    ) {
      if (!payload.isFetchInterval) {
        context.commit('setListQuery', {
          order_no: payload.order_no,
          status: payload.status,
          page: payload.page,
          limit: payload.limit ?? context.state.listQuery.limit,
        })
      }
      if (!payload.isFetchInterval) {
        context.commit('setIsLoading', true)
      }
      const res = await fetchPaibanTaskList(payload)
      const taskList = res.data.archived_task_list as any[]
      if (res && res.status === 1) {
        context.commit('setTaskList', await dealDataToPaibanTaskRow(taskList))
        context.commit('setTotal', res.data.total)
        context.commit('setLiveTotal', res.data.live_total)
        context.commit('setIsLoading', false)
        if (!payload.isFetchInterval) {
          context.dispatch('syncTaskListPolling')
        }
      } else {
        context.commit('setIsLoading', false)
        throw new Error(res.msg)
      }
    },
    /** 按 id 局部更新（暂停、进度变更等） */
    async updateTask(
      context,
      payload: UpdatePaibanTaskPayload & FetchPaibanTaskListPayload
    ) {
      context.commit('setIsLoading', true)
      const res = await updatePaibanTask({
        task: payload.task,
        action: payload.action,
      })
      if (res && res.status === 1) {
        // context.dispatch('fetchPaibanTaskList', {
        //   order_no: payload.order_no,
        //   status: payload.status,
        //   page: payload.page,
        //   limit: payload.limit,
        // })
        if (payload.task.taskStatus !== PaibanTaskStatusEnum.RUNNING) {
          context.dispatch('fetchPaibanTaskList', {
            order_no: payload.order_no,
            status: payload.status,
            page: payload.page,
            limit: payload.limit,
          })
        } else {
          setTimeout(() => {
            context.dispatch('fetchPaibanTaskList', {
              order_no: payload.order_no,
              status: payload.status,
              page: payload.page,
              limit: payload.limit,
            })
          }, 1000)
        }
      } else {
        context.commit('setIsLoading', false)
        throw new Error(res.msg)
      }
    },
  },
}

export default paibanTaskStore
